import { useMemo } from 'react';
import { Flex } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { ProductPromotionClass } from '../../bff-api/products/model';
import { SHIPS_FREE_LIST } from '../../hooks/useIsFreeShipping';
import { Button, Tooltip } from '../../ui-components';
import { sanitizeHtml } from '../content-asset/sanitize-html';
import type { ProductPromotions } from '../../bff-api/products/model';

interface Props {
    id: string;
    promotions: ProductPromotions;
}

const PromotionBadges = ({ id, promotions }: Props): React.ReactElement | null => {
    const promos = useMemo(
        () =>
            promotions
                .filter(
                    ({ promotionId, promotionClass, calloutMsg, details }) =>
                        promotionClass !== ProductPromotionClass.SHIPPING &&
                        !!(calloutMsg || details) &&
                        SHIPS_FREE_LIST.every(
                            (shipsFree) => !promotionId.toLowerCase().includes(shipsFree),
                        ),
                ) // Non ship-free badges
                .slice(0, 2), // Max of 2 badges rendered
        [promotions],
    );

    if (!promos.length) {
        return null;
    }

    return (
        <Flex data-testid={id} gap={2} marginTop={4} wrap="wrap" direction="column">
            {promos.map(({ promotionId, calloutMsg, details }) => {
                const key = `${id}-promotion-${promotionId.replace(/ /g, '').toLowerCase()}`;

                return (
                    <Flex key={key} data-testid={key} gap={1} alignItems="center">
                        <div
                            data-testid={`${key}-message`}
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(calloutMsg) }}
                            className="shrink-0"
                        />

                        {details ? (
                            <Tooltip label={details}>
                                <Button id={`${key}-details`} variant="text-button">
                                    <FormattedMessage
                                        id="promotion.badges.details"
                                        defaultMessage="Details"
                                    />
                                </Button>
                            </Tooltip>
                        ) : null}
                    </Flex>
                );
            })}
        </Flex>
    );
};

export { PromotionBadges };
